import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { PageBackground } from "@/components/ui/page-background";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Music, MapPin, Calendar, Clock, PoundSterling, 
  CheckCircle, XCircle, AlertCircle, Users, FileText 
} from "lucide-react";
import { format } from "date-fns";

export default function BookingConfirmation() {
  const { confirmationUrl } = useParams();
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState<'confirmed' | 'rejected' | null>(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [confirmationUrl]);

  const fetchBookingDetails = async () => {
    try {
      const res = await fetch(`/api/booking-confirmation/${confirmationUrl}`);
      
      if (!res.ok) {
        const data = await res.json();
        if (res.status === 403 && data.status) {
          // Already responded
          setResponse(data.status === 'confirmed' ? 'confirmed' : 'rejected');
          setError(data.message);
        } else {
          setError(data.message || "Failed to load booking details");
        }
        setLoading(false);
        return;
      }
      
      const data = await res.json();
      setBooking(data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      setError("Failed to load booking details");
      setLoading(false);
    }
  };

  const handleResponse = async (responseType: 'confirmed' | 'rejected') => {
    setResponding(true);
    try {
      const res = await fetch(`/api/booking-confirmation/${confirmationUrl}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: responseType }),
      });
      
      if (res.ok) {
        // Successfully responded - show confirmation page
        setResponse(responseType);
        setError(null);
      } else {
        // Handle error response
        try {
          const data = await res.json();
          setError(data.message || "Failed to process response");
        } catch {
          setError("Failed to process response");
        }
      }
    } catch (error) {
      console.error("Failed to respond:", error);
      setError("Failed to process response");
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <PageBackground>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading booking details...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (error && !booking) {
    return (
      <PageBackground>
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-md w-full bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-amber-600" />
              </div>
              <CardTitle className="text-center">Booking Confirmation</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  {error}
                </AlertDescription>
              </Alert>
              {response && (
                <div className="mt-4 text-center">
                  <Badge 
                    variant={response === 'confirmed' ? 'default' : 'destructive'}
                    className="text-lg px-4 py-2"
                  >
                    {response === 'confirmed' ? 'Booking Confirmed' : 'Booking Rejected'}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageBackground>
    );
  }

  if (response) {
    return (
      <PageBackground>
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-md w-full bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                {response === 'confirmed' ? (
                  <CheckCircle className="h-16 w-16 text-green-600" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-600" />
                )}
              </div>
              <CardTitle className="text-center text-2xl">
                {response === 'confirmed' ? 'Booking Confirmed!' : 'Booking Rejected'}
              </CardTitle>
              <CardDescription className="text-center mt-2">
                {response === 'confirmed' 
                  ? 'Thank you for confirming your booking. You will receive further details via email.'
                  : 'The booking has been rejected. The organizer will be notified.'}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <Card className="max-w-2xl w-full bg-white/20 backdrop-blur-xl border-white/30">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <Music className="h-12 w-12 text-purple-600" />
            </div>
            <CardTitle className="text-center text-2xl">Booking Confirmation Request</CardTitle>
            <CardDescription className="text-center">
              Please review the booking details and confirm or reject
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Act Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Act Details
              </h3>
              <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 space-y-2">
                <p className="font-medium text-lg">{booking?.act?.name}</p>
                {booking?.act?.type && (
                  <Badge variant="secondary">{booking.act.type}</Badge>
                )}
                {booking?.act?.genre && (
                  <p className="text-sm text-slate-600">Genre: {booking.act.genre}</p>
                )}
                {booking?.act?.participants?.length > 0 && (
                  <div className="text-sm text-slate-600">
                    <p className="font-medium">Members:</p>
                    <ul className="ml-4">
                      {booking.act.participants.map((p: any, i: number) => (
                        <li key={i}>• {p.name} {p.role && `(${p.role})`}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Event Details */}
            {booking?.event && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Event Details
                </h3>
                <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 space-y-2">
                  <p className="font-medium">{booking.event.name}</p>
                  {booking.event.date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-slate-600" />
                      <span>{format(new Date(booking.event.date), 'EEEE, MMMM d, yyyy')}</span>
                    </div>
                  )}
                  {booking.booking.performanceTime && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-slate-600" />
                      <span>
                        Performance: {format(new Date(booking.booking.performanceTime), 'h:mm a')}
                        {booking.booking.durationMinutes && ` (${booking.booking.durationMinutes} minutes)`}
                      </span>
                    </div>
                  )}
                  {booking.event.doorsOpen && (
                    <p className="text-sm text-slate-600">
                      Doors: {format(new Date(booking.event.doorsOpen), 'h:mm a')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Venue Details */}
            {booking?.venue && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  Venue
                </h3>
                <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 space-y-1">
                  <p className="font-medium">{booking.venue.name}</p>
                  {booking.venue.address && (
                    <p className="text-sm text-slate-600">{booking.venue.address}</p>
                  )}
                  {(booking.venue.city || booking.venue.postcode) && (
                    <p className="text-sm text-slate-600">
                      {[booking.venue.city, booking.venue.postcode].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Financial Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <PoundSterling className="h-5 w-5 text-green-600" />
                Financial Terms
              </h3>
              <div className="bg-green-50/50 backdrop-blur-sm rounded-lg p-4 space-y-2 border border-green-200/50">
                {booking?.booking?.fee && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Performance Fee:</span>
                    <span className="text-lg font-bold text-green-700">
                      £{(booking.booking.fee / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                {booking?.booking?.commissionAmount && booking.booking.commissionAmount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span>
                      Agency Commission 
                      {booking.booking.commissionType === 'percentage' && booking.booking.commissionValue
                        ? ` (${(booking.booking.commissionValue / 100).toFixed(1)}%)`
                        : ' (Flat)'}:
                    </span>
                    <span className="text-red-600">
                      -£{(booking.booking.commissionAmount / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                {booking?.booking?.fee && booking?.booking?.commissionAmount && (
                  <div className="pt-2 border-t border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Net Payment:</span>
                      <span className="text-lg font-bold text-green-700">
                        £{((booking.booking.fee - (booking.booking.commissionAmount || 0)) / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                {booking?.booking?.hasDeposit && booking?.booking?.depositAmount && (
                  <div className="pt-2 border-t border-green-200">
                    <div className="flex justify-between items-center text-sm">
                      <span>Deposit Required:</span>
                      <span className="font-medium">
                        £{(booking.booking.depositAmount / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {booking?.booking?.notes && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-slate-600" />
                  Additional Notes
                </h3>
                <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4">
                  <p className="text-sm whitespace-pre-wrap">{booking.booking.notes}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => handleResponse('confirmed')}
                disabled={responding}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Booking
              </Button>
              <Button
                onClick={() => handleResponse('rejected')}
                disabled={responding}
                variant="destructive"
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject Booking
              </Button>
            </div>

            {error && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </PageBackground>
  );
}